import React, {useEffect, useState} from "react";
import {Box, Chip, Grid, MenuItem, Paper, Typography} from "@material-ui/core";
import {MUIAutoComplete, MUIInput, MUISelect} from "../../utils/formUtils";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Button from "@material-ui/core/Button";
import ReCAPTCHA from "react-google-recaptcha";
import axiosInstance from "../../api";
import {Alert} from "@material-ui/lab";
import Spinner from "../../components/Spinner";
import addressData from "../../data/addressData.json";
import {BusinessRounded, ContactMailRounded, PermIdentityRounded} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import AgentAgreement from "./AgentAgreement";
import GoogleMaps from "../../utils/GooglePlacesInput";
import {
    countryConversion,
    getAddressObject,
    handleCountryConversion,
    handleCountyConversion
} from "../../utils/fmapUtils";

const validationSchema = yup.object().shape({
    dfsLicenseNumber: yup.string().required("Please complete all required fields").nullable(),
    lastFourSSN: yup.string().required("Please complete all required fields").nullable(),
    ressn: yup.string().oneOf([yup.ref('lastFourSSN'), null], 'Last four SSN must match').nullable().required("Please complete all required fields"),
    workPhone: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().phone("US", false,"Primary Phone must be a valid US phone number.").required('Primary Phone must be a valid US phone number.').nullable(),
        otherwise: yup.string().nullable()
    }),
    emailAddress: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().email('Email should be in valid format').lowercase().required("Email required").nullable(),
        otherwise: yup.string().nullable()
    }),
    emailCopy: yup.string().email('Email should be in valid format').lowercase().oneOf([yup.ref('emailAddress'), null], 'Email must match'),
    // addressLine1: yup.string().when('agentFirstName', {
    //     is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
    //     then: yup.string().required("Address Line 1 required.").nullable(),
    //     otherwise: yup.string().nullable()
    // }),
    city: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().required("City required.").nullable(),
        otherwise: yup.string().nullable()
    }),
    county: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().required("County required.").nullable(),
        otherwise: yup.string().nullable()
    }),
    zip: yup.number().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.number().typeError('Zip must be a valid code').required('Zip must be a valid code').positive('Zip must be a valid code').integer(),
        otherwise: yup.number().nullable()
    }),
    agencyName: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().required("Agency Name required.").nullable(),
        otherwise: yup.string().nullable()
    }),
    state: yup.string().when('agentFirstName', {
        is: (agentFirstName) => agentFirstName !== undefined && agentFirstName !== null,
        then: yup.string().required("State required.").nullable(),
        otherwise: yup.string().nullable()
    })
});
export default function AgentRegistrationGP() {
    const history = useHistory();
    const {handleSubmit, control, setValue, clearErrors ,reset,formState: {errors}} = useForm({resolver: yupResolver(validationSchema)});
    const [acceptTC, setAcceptTC] = useState(false);
    const [greCaptcha, setGreCaptcha] = useState(null);
    const [registrationAttributes, setRegistrationAttributes] = useState({city: '', county: ''});
    const [errorMessage, setErrorMessage] = useState(null);
    const [hasInitError, setHasInitError] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [fetchSuccess, setFetchSuccess] = useState(false);
    const [userEmailCopy, setUserEmailCopy] = useState('');
    const [cityList, setCityList] = useState([]);
    const [countyList, setCountyList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [ressn, setRessn] = useState(null);

    const recaptchaRef = React.createRef();


    useEffect(() => {
        // Anything in here is fired on component mount.
        // fetching static data
        setCountyList(['Out of Florida', ...addressData.county]);
        setStateList([{"label": 'Out of Florida', "value":"OF"}, ...addressData.state]);
        setCountryList(addressData.usCountry);
    }, []);


    const onSubmit = data => {
        setErrorMessage(null);
        if (greCaptcha && acceptTC) {
            if (!fetchSuccess) {
                initalCheck(data);
            } else {
                if ((data.state === 'FL' && data.county === 'Out of Florida') || (data.state !== 'FL' && data.county !== 'Out of Florida')) {
                    setErrorMessage('State/ County combination invalid. Please select a valid County.');
                    return
                }

                if (!performFieldValidation()) return;
                submitData();
            }
        } else {
            setHasInitError(true);
        }
    };

    function performFieldValidation() {
        if (!registrationAttributes.addressLine1) {
            setErrorMessage('Address Line 1 required.');
            return false;
        } else {
            setErrorMessage(null);
        }
        return true;
    }

    function submitData() {
        setShowSpinner(true);
        setErrorMessage(undefined);
        setHasInitError(false);
        updateState({"greCaptcha": greCaptcha});
        axiosInstance.post("unauth/agents/registration/submit", registrationAttributes)
            .then(data => {
                if (data && data?.responseCode == -200) {
                    history.push("/agentConfirm/-200");
                } else if (data && data?.responseCode < 0) {
                    setGreCaptcha(null);
                    setErrorMessage("The license number and social security number you've entered does not match DFS records.  Please re-enter the data.");
                } else {
                    history.push("/agentConfirm/100");
                }
            })
            .catch(error => {
                setErrorMessage("An error has occurred. Please try again later.");
            })
            .finally(() => setShowSpinner(false));
    }

    function initalCheck(data) {
        setShowSpinner(true);
        setErrorMessage(undefined);
        setHasInitError(false);
        data["greCaptcha"] = greCaptcha;
        axiosInstance.post("unauth/agents/registration/validate", data)
            .then(data => {
                if (data && data?.responseCode === -200) {
                    setGreCaptcha(null);
                    setErrorMessage("The license number and social security number you've entered does not match DFS records.  Please re-enter the data.");
                } else if (data && data?.responseCode === -100) {
                    setGreCaptcha(null);
                    setErrorMessage("Agents must have a minimum appointments to participate in FMAP. Please see the Florida Market Assistance Plan Agent User Guide.");
                    updateState({"appointmentList": data?.appointmentList});
                    // window.grecaptcha.reset();
                } else if (data && data?.responseCode === -400) {
                    setGreCaptcha(null);
                    setErrorMessage("Agent is terminated from Citizens (with cause) and cannot use the FMAP online application.");
                } else if (data && data?.responseCode === -500) {
                    setGreCaptcha(null);
                    setErrorMessage("User already exist in FMAP application. Please reach out to us using the contact us feature.");
                } else if (data && data?.responseCode === 100) {
                    setFetchSuccess(true);
                    updateState({
                        "agentFirstName": data.agentFirstName,
                        "agentLastName": data.agentLastName,
                        "emailAddress": data.emailAddress,
                        "agencyName": data.agencyName,
                        "workPhone": data.workPhone,
                        "altPhone": data.altPhone,
                        "addressLine1": data?.officeAddress?.addressLine1,
                        "addressLine2": data?.officeAddress?.addressLine2,
                        "city": data?.officeAddress?.city,
                        "state": data?.officeAddress?.state,
                        "county": data?.officeAddress?.county,
                        "country": data?.officeAddress?.country,
                        "zip": data?.officeAddress?.zip,
                        "appointmentList": undefined,
                        "greCaptcha": greCaptcha,
                        "nonce":data?.nonce
                    });
                } else if (data && data?.isValidData === false) {
                    setGreCaptcha(null);
                    setErrorMessage("The DFS License # and SSN combo does not match. Please call Citizens at 866.411.2742 to discuss further.");
                    // window.grecaptcha.reset();
                }
            })
            .catch(error => {
                setErrorMessage("An error has occurred. Please try again later.");
            })
            .finally(() => setShowSpinner(false));
    }

    function handleFormChange(event) {
        setRegistrationAttributes((prevState) => ({
            ...prevState,
            [event.target.name]: event.target.value
        }));
    }

    function updateState(newValue) {
        setRegistrationAttributes((prevState) => ({
            ...prevState, ...newValue
        }));
    }

    function onChange(value) {
        setGreCaptcha(value);
    }

    function blockFieldCopy(event) {
        event.preventDefault();
    }

    const populateValues = async (data) => {
        //console.log(data[0].address_components)

        if( data && data[0] && data[0].address_components){
            debugger
            const addressValue =  getAddressObject(data[0].address_components)
            let countyValue = (addressValue.region && addressValue.region === 'Florida') ? handleCountyConversion( addressValue.county, countyList) : 'Out of Florida';
            let stateValue = stateList?.filter(x => x.label === addressValue.region)[0]?.value;
            stateValue = (stateValue) ? stateValue : 'OF';
            debugger
            updateState({
                addressLine1: addressValue.home + ' ' + addressValue.street,
                city: addressValue.city,
                state: stateValue,
                county: countyValue,
                zip: addressValue.postal_code,
                country: handleCountryConversion(addressValue.country)
            });
            setValue('city', addressValue.city);
            setValue('state', stateValue);
            setValue('county', countyValue);
            setValue('zip',addressValue.postal_code);
            setValue('country',addressValue.country);
            setValue('addressLine1',addressValue.home + ' ' + addressValue.street);
        }
    }

    const resetValues = () => {
        debugger
        updateState({addressLine1: '', addressLine2:'', city:'', state:'OF', county:'Out of Florida', zip:''});
        setValue('addressLine1','');
    }


    if (showSpinner) return <Spinner/>

    if (!acceptTC) return <AgentAgreement setAcceptTC={setAcceptTC}/>

    return (
        <section style={{backgroundColor: "#eaeef3", paddingBottom: 30}}>
            <Box m={1} marginBottom={0}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box m={3} marginBottom={0}>
                        <Paper style={{padding: 16}}>
                            <Typography variant="h5" component="h2">
                                Agent Information <PermIdentityRounded/>
                            </Typography><br/>
                            {!fetchSuccess && <><Box component="span" display="flex"
                                                     style={{backgroundColor: "lightblue", marginBottom: 5}} p={2}>
                                Enter your state of Florida agent license number and last four digits of your Social Security number twice to retrieve your license details from the Florida Department of Financial Services (DFS).
                            </Box>
                                <br/>
                            </>}
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <MUIInput control={control} name="dfsLicenseNumber" disabled={fetchSuccess}
                                              inputPrp={{maxLength: 7}}
                                              defaultValue={registrationAttributes?.dfsLicenseNumber}
                                              value={registrationAttributes?.dfsLicenseNumber} label="DFS License#"
                                              onInputChange={handleFormChange} errors={errors}/>

                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <MUIInput control={control} name="lastFourSSN" type="password"
                                              inputPrp={{maxLength: 4}}
                                              disabled={fetchSuccess}
                                              defaultValue={registrationAttributes?.lastFourSSN}
                                              value={registrationAttributes?.lastFourSSN} label="Last 4 of SSN"
                                              onInputChange={handleFormChange} errors={errors}/>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <MUIInput control={control} name="ressn" type="password" inputPrp={{maxLength: 4}}
                                              disabled={fetchSuccess}
                                              defaultValue={ressn} onInputChange={data => setRessn(data.target.value)}
                                              value={ressn} label="Re-type last 4 of SSN"
                                              errors={errors}/>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    {errorMessage && registrationAttributes && registrationAttributes.appointmentList && registrationAttributes.appointmentList.length > 0 &&
                                    <Box>
                                        Appointment List : {registrationAttributes.appointmentList.map(x => {
                                        return <Chip style={{marginRight: 3}} size="small" color="primary" label={x}/>
                                    })}
                                    </Box>}
                                </Grid>
                                {!fetchSuccess &&
                                <>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <ReCAPTCHA ref={recaptchaRef}
                                                   sitekey={process.env.REACT_APP_GOOGLE_CAPTCHA_KEY}
                                                   onChange={onChange}
                                                   onExpired={() => {
                                                       setGreCaptcha(null);
                                                       recaptchaRef.current.reset();
                                                   }}
                                        /> <br/>

                                    </Grid>
                                    {/*<Grid item xs={12} sm={12} md={12}>*/}
                                    {/*    <FormGroup>*/}
                                    {/*        <FormControlLabel*/}
                                    {/*            control={<Checkbox color="primary" checked={acceptTC} onChange={(event) => {*/}
                                    {/*                setAcceptTC(event.target.checked);*/}
                                    {/*            }}/>} label={*/}
                                    {/*            <>*/}
                                    {/*                <span>I accept to the </span>*/}
                                    {/*                <a href="/agentAgreement" target="_blank">Agent Terms and Agreement</a>*/}
                                    {/*            </>*/}
                                    {/*        }/>*/}
                                    {/*    </FormGroup>*/}
                                    {/*</Grid>*/}
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Box component="span" justifyContent="flex-end" alignItems="flex-end"
                                             display="flex">
                                            <br/>
                                            <Button type="submit" variant="contained" color="primary" name="btnNext">
                                                Next
                                            </Button>
                                        </Box>
                                    </Grid>
                                    {errorMessage && <Grid item xs={12} sm={12} md={12}>
                                        <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">{errorMessage}</Alert>
                                        </div>
                                    </Grid>}
                                    {hasInitError && <Grid item xs={12} sm={12} md={12}>
                                        <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">Please accept the <i>Captcha</i> to proceed further.</Alert>
                                        </div>
                                    </Grid>}

                                </>}
                            </Grid>
                        </Paper>
                        {fetchSuccess &&
                        <>
                            <Paper style={{padding: 16, marginTop: 10}}>
                                <Typography variant="h5" component="h2">
                                    Contact Details <ContactMailRounded/>
                                </Typography><br/>
                                <Box component="span" display="flex"
                                     style={{backgroundColor: "lightblue", marginBottom: 5}} p={2}>
                                    Enter your email address and phone number(s). The email address you enter will be used to alert you when consumer requests match your saved eligibility criteria, as well as remind you of your login ID and password in the event you lose it. FMAP obtains some of your information from the Florida Department of Financial Services (DFS). If any of the prefilled data below is not accurate, please update with the correct information. Any prefilled data that is greyed out cannot be edited on this screen. Please contact DFS to update that information.
                                </Box>
                                <br/>
                                <Grid container alignItems="flex-start" spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="agentFirstName" inputPrp={{maxLength: 50}}
                                                  disabled
                                                  defaultValue={registrationAttributes?.agentFirstName ?? ''}
                                                  value={registrationAttributes?.agentFirstName} label="First & Middle Name"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="agentLastName" inputPrp={{maxLength: 50}}
                                                  disabled
                                                  defaultValue={registrationAttributes?.agentLastName}
                                                  value={registrationAttributes?.agentLastName} label="Last Name"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="emailAddress" onInputCopy={blockFieldCopy}
                                                  onInputCut={blockFieldCopy}
                                                  defaultValue={registrationAttributes?.emailAddress}
                                                  inputPrp={{maxLength: 50}}
                                                  value={registrationAttributes?.emailAddress} label="Email"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="emailCopy" inputPrp={{maxLength: 50}}
                                                  onInputPaste={blockFieldCopy}
                                                  defaultValue={userEmailCopy}
                                                  value={userEmailCopy} label="Reenter Email"
                                                  onInputChange={(data) => setUserEmailCopy(data.target.value)}
                                                  errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="workPhone" inputPrp={{maxLength: 15}}
                                                  defaultValue={registrationAttributes?.workPhone}
                                                  value={registrationAttributes?.workPhone} label="Primary Phone"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="altPhone" inputPrp={{maxLength: 15}}
                                                  defaultValue={registrationAttributes?.altPhone}
                                                  value={registrationAttributes?.altPhone} label="Alternate Phone"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}></Grid>
                                    <Grid item xs={12} sm={6} md={4}></Grid>
                                </Grid>
                            </Paper>
                            <Paper style={{padding: 16, marginTop: 10}}>
                                <Typography variant="h5" component="h2">
                                    Location Details <BusinessRounded/>
                                </Typography><br/>
                                <Grid container alignItems="flex-start" spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="agencyName"
                                                  defaultValue={registrationAttributes?.agencyName}
                                                  inputPrp={{maxLength: 100}}
                                                  value={registrationAttributes?.agencyName} label="Agency Name"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <GoogleMaps control={control} name="addressLine1" inputPrp={{maxLength: 200}} onValuChange={(newValue) =>
                                        {
                                            registrationAttributes.addressLine1 = newValue
                                        }} valueOnLoad={registrationAttributes?.addressLine1}
                                                    label="Address Line 1" errors={errors}
                                                    callback={populateValues} resetCallback={resetValues}/>
                                        {/*<MUIInput control={control} name="addressLine1" inputPrp={{maxLength: 200}}*/}
                                        {/*          defaultValue={registrationAttributes?.addressLine1}*/}
                                        {/*          value={registrationAttributes?.addressLine1} label="Address Line 1"*/}
                                        {/*          onInputChange={handleFormChange} errors={errors}/>*/}
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="addressLine2" inputPrp={{maxLength: 200}}
                                                  defaultValue={registrationAttributes?.addressLine2}
                                                  value={registrationAttributes?.addressLine2} label="Address Line 2"
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        {/*<MUIAutoComplete id="city" control={control} name="city" label="City"*/}
                                        {/*                 errors={errors}*/}
                                        {/*                 value={registrationAttributes?.city}*/}
                                        {/*                 onValueChange={handleFormChange} options={cityList}/>*/}
                                        <MUIInput control={control} name="city" inputPrp={{maxLength: 200}}
                                                  defaultValue={registrationAttributes?.city}
                                                  value={registrationAttributes?.city} label="City"
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIAutoComplete id="county" control={control} name="county" label="County"
                                                         errors={errors}
                                                         value={registrationAttributes?.county}
                                                         onValueChange={handleFormChange} options={countyList}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUIInput control={control} name="zip"
                                                  defaultValue={registrationAttributes?.zip}
                                                  inputPrp={{maxLength: 5}}
                                                  value={registrationAttributes?.zip} label="ZIP"
                                                  error={registrationAttributes?.errors?.zip}
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUISelect control={control} errors={errors} id="state" name="state"
                                                   label="State"
                                                   value={registrationAttributes?.state}
                                                   defaultValue={registrationAttributes?.state}
                                                   onFormChange={handleFormChange}>
                                            {stateList.map(item => <MenuItem key={item.label}
                                                                             value={item.value}>{item.label}</MenuItem>
                                            )}
                                        </MUISelect>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <MUISelect control={control} errors={errors} id="country" name="country"
                                                   label="Country"
                                                   value={registrationAttributes?.country}
                                                   defaultValue={registrationAttributes?.country}
                                                   onFormChange={handleFormChange}>
                                            {countryList.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>
                                            )}
                                        </MUISelect>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Box component="span" justifyContent="flex-end" alignItems="flex-end"
                                             display="flex">
                                            <br/>
                                            <Button type="submit" variant="contained" color="primary" name="btnNext">
                                                Submit
                                            </Button>
                                        </Box>
                                    </Grid>
                                    {errorMessage && <Grid item xs={12} sm={12} md={12}>
                                        <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">{errorMessage}</Alert>
                                        </div>
                                    </Grid>}
                                    {hasInitError && <Grid item xs={12} sm={12} md={12}>
                                        <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">Please accept the <i>Agent
                                                Agreement</i> and <i>Captcha</i> to proceed further.</Alert>
                                        </div>
                                    </Grid>}
                                </Grid>
                            </Paper>
                        </>
                        }
                    </Box>
                </form>
            </Box>
        </section>
    );
}
